import { useQuery } from '@apollo/client';
import { confirm } from '@components/ConfirmModal/confirm';
import Colors from '@constants/Colors';
import { useAuth } from '@contexts/AuthContext';
import { GET_PRO } from '@gql/pros';
import { CreditsFeature, hasEnoughCredits } from '@utils/permissions';
import { Roles } from '@utils/roleUtils';
import { isArray } from 'lodash';
import React, { useCallback } from 'react';

type Props = {
    children?: React.ReactNode;
    roles?: Roles[];
    loading?: boolean;
    feature?: CreditsFeature | CreditsFeature[];
    showLockIcon?: boolean;
    hideIfDisabled?: boolean;
    proId: string;
    alert?: {
        title: string;
        message: React.ReactNode;
    };
};

const LockIcon = () => {
    return (
        <i
            className="fi fi-br-lock"
            style={{
                fontSize: 20,
                position: 'absolute',
                top: '50%',
                right: 8,
                color: Colors.NEUTRAL_GRAY,
                transform: 'translate(50%, -50%)',
            }}
        />
    );
};

const FeatureCreditsDisabler = (props: Props) => {
    const { loading, data } = useQuery(GET_PRO, {
        variables: { proId: props.proId },
        skip: !props.proId,
    });
    const { user } = useAuth();

    const showAlert = useCallback(
        async e => {
            e.preventDefault();
            await confirm({
                confirmation: props.alert.title,
                content: props.alert.message,
                acknowledgement: true,
                options: {
                    confirmText: 'OK',
                },
            });
        },
        [props.alert],
    );

    if (loading || !user) {
        return null;
    }

    if (props.roles && !props.roles.includes(user.role)) {
        return null;
    }

    if (props.roles && !props.feature) {
        return <React.Fragment>{props.children}</React.Fragment>;
    }

    if (!props.proId) {
        return (
            <React.Fragment>
                {React.Children.map(props.children, child => {
                    return (
                        <>
                            {React.cloneElement(child as React.ReactElement, {
                                disabled: props.loading || loading,
                            })}
                        </>
                    );
                })}
            </React.Fragment>
        );
    }

    if (isArray(props.feature) && [...props.feature].every(feature => !hasEnoughCredits(data?.getPro?.credits, feature))) {
        // add disabled attribute to the children
        // add lock icon to the children
        if (props.hideIfDisabled) {
            return null;
        }
        return (
            <React.Fragment>
                {React.Children.map(props.children, child => {
                    return (
                        <div style={{ position: 'relative', display: 'inline' }}>
                            {React.cloneElement(child as React.ReactElement, {
                                disabled: !props.alert || props.loading || loading,
                                onClick: showAlert,
                            })}
                            {props.showLockIcon && <LockIcon />}
                        </div>
                    );
                })}
            </React.Fragment>
        );
    }

    if (!isArray(props.feature) && !hasEnoughCredits(data?.getPro?.credits, props.feature)) {
        // add disabled attribute to the children
        // add lock icon to the children
        if (props.hideIfDisabled) {
            return null;
        }
        return (
            <React.Fragment>
                {React.Children.map(props.children, child => {
                    return (
                        <div style={{ position: 'relative' }}>
                            {React.cloneElement(child as React.ReactElement, {
                                disabled: !props.alert || props.loading || loading,
                                onClick: showAlert,
                            })}
                            {props.showLockIcon && <LockIcon />}
                        </div>
                    );
                })}
            </React.Fragment>
        );
    }
    return (
        <React.Fragment>
            {React.Children.map(props.children, child => {
                return (
                    <>
                        {React.cloneElement(child as React.ReactElement, {
                            disabled: props.loading || loading,
                        })}
                    </>
                );
            })}
        </React.Fragment>
    );
};

// react hook with similar functionality
export const useHasFeatureCredits = (feature: CreditsFeature | CreditsFeature[]) => {
    const { loading, data } = useQuery(GET_PRO, {
        variables: { proId: '' }, // proId is required but not used in this hook, so we can pass an empty string
    });
    const { user } = useAuth();

    if (loading || !user) {
        return false;
    }

    if (isArray(feature) && [...feature].every(feature => !hasEnoughCredits(data?.getPro?.credits, feature))) {
        return false;
    }

    if (!isArray(feature) && !hasEnoughCredits(data?.getPro?.credits, feature)) {
        return false;
    }
    return true;
};

export default FeatureCreditsDisabler;
